import React, { useEffect, useRef, useState } from "react";
import { Amplify } from "aws-amplify";
import { USER_AUTH_CONFIG } from "../config/aws-config";
import { saveAuthRelay } from "./user-login-service";
import {
  clearUserBrowserSession,
  waitForUserRelayAuthState,
} from "./user-auth-session";
import {
  Alert,
  Button,
  Box,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { UserAuthCard, UserAuthShell } from "./user-auth-layout";

import { userLoginError } from "./user-login-errors";

const USER_CALLBACK_INTENT_KEY = "mhome.pampas.user.callback-relay.v1";
const USER_LOGIN_CONTEXT_KEY = "mhome.pampas.user.login-context.v1";
const USER_CALLBACK_INTENT_TTL_MILLIS = 5 * 60 * 1000;

const UserLoginCallback = () => {
  const callbackStarted = useRef(false);
  const callbackInFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (callbackStarted.current) return;
    callbackStarted.current = true;
    handleCallback();
  }, []);

  const handleCallback = async () => {
    if (callbackInFlight.current) return;
    callbackInFlight.current = true;
    setBusy(true);
    setFailure("");
    try {
      await completeCallback();
    } catch (error) {
      setFailure(userLoginError(error, "oauth-session"));
    } finally {
      callbackInFlight.current = false;
      setBusy(false);
    }
  };

  const completeCallback = async () => {
    let context;
    try {
      context = JSON.parse(
        sessionStorage.getItem(USER_LOGIN_CONTEXT_KEY) || "{}"
      );
    } catch (_) {
      context = {};
    }
    const flow = context.flow;
    const relayid = context.relayId;
    const pairingCode = context.pairingCode;
    let authState;
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      // Amplify 6 exchanges the Hosted UI authorization code asynchronously.
      // Wait for that exchange before reading the relay credentials.
      authState = await waitForUserRelayAuthState();
    } catch (error) {
      setFailure(userLoginError(error, "oauth-session"));
      return;
    }

    const returnPath = sessionStorage.getItem("cognito_return_path");
    sessionStorage.removeItem("cognito_return_path");
    if (isOAuthReturnPath(returnPath)) {
      window.location.replace(returnPath);
      return;
    }

    try {
      if (flow === "callback") {
        let intent;
        try {
          intent = JSON.parse(
            sessionStorage.getItem(USER_CALLBACK_INTENT_KEY) || "{}"
          );
        } catch (_) {
          intent = {};
        }
        if (
          !intent.relayId ||
          !intent.loginTicket ||
          intent.relayId !== relayid ||
          Number(intent.createdAt) + USER_CALLBACK_INTENT_TTL_MILLIS <=
            Date.now()
        ) {
          sessionStorage.removeItem(USER_CALLBACK_INTENT_KEY);
          throw Object.assign(new Error("Invalid login request"), {
            code: "BAD_REQUEST",
          });
        }
        const resp = await saveAuthRelay({
          ...authState,
          flow: "callback",
          relayId: intent.relayId,
          loginTicket: intent.loginTicket,
        });
        if (!resp.redirectUri) {
          throw new Error("Missing login callback.");
        }
        sessionStorage.removeItem(USER_CALLBACK_INTENT_KEY);
        sessionStorage.removeItem(USER_LOGIN_CONTEXT_KEY);
        try {
          await clearUserBrowserSession();
        } finally {
          window.location.replace(resp.redirectUri);
        }
      } else if (flow === "pairing" && relayid && pairingCode) {
        await saveAuthRelay({
          ...authState,
          flow: "pairing",
          relayId: relayid,
          pairingCode,
        });
        sessionStorage.removeItem(USER_LOGIN_CONTEXT_KEY);
        window.location.href = "/auth-granted";
      } else {
        throw Object.assign(new Error("Invalid login request"), {
          code: "BAD_REQUEST",
        });
      }
    } catch (error) {
      setFailure(userLoginError(error, "delivery"));
    }
  };

  return (
    <UserAuthShell maxWidth={440}>
      <UserAuthCard contentSx={{ p: { xs: 3.5, sm: 4.5 } }}>
        <Stack spacing={2.25} alignItems="center" textAlign="center">
          <Box
            sx={{
              width: 64,
              height: 64,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              bgcolor: "#edf6f8",
              border: "1px solid #d8e9ee",
            }}
          >
            {busy && <CircularProgress size={30} thickness={4} />}
          </Box>
          <Box>
            <Typography
              component="h1"
              sx={{ color: "#253940", fontSize: 25, fontWeight: 700 }}
            >
              {failure ? "Connection not completed" : "Finishing sign-in"}
            </Typography>
            <Typography sx={{ mt: 1, color: "#71838b", lineHeight: 1.6 }}>
              {failure
                ? "You can retry below. If the request has expired, return to the app to start again."
                : "Keep this window open while we complete your connection."}
            </Typography>
          </Box>
          {failure && (
            <>
              <Alert severity="error">{failure}</Alert>
              <Button
                variant="contained"
                disabled={busy}
                onClick={handleCallback}
              >
                Retry connection
              </Button>
            </>
          )}
        </Stack>
      </UserAuthCard>
    </UserAuthShell>
  );
};

function isOAuthReturnPath(value) {
  if (!value) return false;
  try {
    const url = new URL(value, window.location.origin);
    return (
      url.origin === window.location.origin &&
      url.pathname === "/oauth/authorize" &&
      Boolean(url.searchParams.get("transaction_id"))
    );
  } catch (_) {
    return false;
  }
}

export default UserLoginCallback;
